import "@/App.css";
import "@/styles/theme-light.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import HomePage from "@/pages/HomePage";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/authContext";
import { ThemeProvider } from "@/lib/themeContext";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import ImpersonationBanner from "@/components/ImpersonationBanner";

// Code-split heavy routes — they don't ship to mobile visitors landing on the homepage.
const ApplyPage = lazy(() => import("@/pages/ApplyPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const PortalLoginPage = lazy(() => import("@/pages/PortalLoginPage"));
const PortalMagicPage = lazy(() => import("@/pages/PortalMagicPage"));
const PortalOfferPage = lazy(() => import("@/pages/PortalOfferPage"));
const PortalGuestPage = lazy(() => import("@/pages/PortalGuestPage"));
const PortalSignupPage = lazy(() => import("@/pages/PortalSignupPage"));
const PortalPage = lazy(() => import("@/pages/PortalPage"));
const EmployeePortalPage = lazy(() => import("@/pages/EmployeePortalPage"));
const PlaybookPage = lazy(() => import("@/pages/PlaybookPage"));
const EmployeeSettingsPage = lazy(() => import("@/pages/EmployeeSettingsPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const OfferAcceptPage = lazy(() => import("@/pages/OfferAcceptPage"));
const ApplicationSlugPage = lazy(() => import("@/pages/ApplicationSlugPage"));
const LongApplicationPage = lazy(() => import("@/pages/LongApplicationPage"));
const ThankYouPage = lazy(() => import("@/pages/ThankYouPage"));

const FAVICON_DATA_URI =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#0052FF"/><stop offset="1" stop-color="#00FFA3"/></linearGradient></defs><rect width="64" height="64" rx="14" fill="url(#g)"/><path d="M36 12L18 36h10l-2 16 18-24H34l2-16z" fill="#000"/></svg>`
  );

function App() {
  useEffect(() => {
    // Iter 139 — global axios interceptor. Every request to
    // /api/admin/* now automatically carries the caller's Bearer JWT
    // (if any) alongside the legacy X-Admin-Token. This is what the
    // backend uses in `_scope_from_bearer` to team-scope managers so
    // e.g. William doesn't see 468 global apps on the CRM dashboard —
    // only his book + his direct reports'.
    // Guarded with `__pffAdminBearer` so hot-reload doesn't stack
    // interceptors.
    if (!axios.__pffAdminBearer) {
      axios.interceptors.request.use((config) => {
        try {
          const url = (config?.url || "").toString();
          const isAdminApi = url.includes("/api/admin/");
          if (!isAdminApi) return config;
          const jwt = localStorage.getItem("pff_auth_token") || "";
          if (!jwt) return config;
          config.headers = config.headers || {};
          // Don't clobber an explicit Authorization on a request that
          // already set one (e.g. exchange-token uses its own JWT).
          if (!config.headers.Authorization && !config.headers.authorization) {
            config.headers.Authorization = `Bearer ${jwt}`;
          }
        } catch { /* ignore */ }
        return config;
      });
      axios.__pffAdminBearer = true;
    }
    // Override platform-set title and favicon
    document.title = "Power Forward Funding — Business Funding Built For Momentum";
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.type = "image/svg+xml";
    link.href = FAVICON_DATA_URI;

    let apple = document.querySelector("link[rel='apple-touch-icon']");
    if (!apple) {
      apple = document.createElement("link");
      apple.rel = "apple-touch-icon";
      document.head.appendChild(apple);
    }
    apple.href = FAVICON_DATA_URI;

    // Update meta description
    let desc = document.querySelector("meta[name='description']");
    if (!desc) {
      desc = document.createElement("meta");
      desc.name = "description";
      document.head.appendChild(desc);
    }
    desc.content = "Power Forward Funding — Business funding built for momentum. Same-day approvals. Funding up to $5M. 24-hour wires.";
  }, []);

  return (
    <div className="App">
      <AppErrorBoundary>
        <ThemeProvider>
          <AuthProvider>
            <BrowserRouter>
              <ImpersonationBanner />
              <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A] grid place-items-center text-zinc-500 text-sm font-mono">Loading…</div>}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/apply" element={<LongApplicationPage />} />
                  {/* Iter 134 (Feb 2026) — reps' unique attribution
                      links `/apply/{slug}` now route to the FULL
                      long-form application. The progressive form is
                      retained at `/apply/legacy` for anyone with an
                      old bookmark. */}
                  <Route path="/apply/legacy" element={<ApplyPage />} />
                  <Route path="/apply/:repSlug" element={<LongApplicationPage />} />
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/portal" element={<PortalPage />} />
                  <Route path="/portal/employee" element={<EmployeePortalPage />} />
                  <Route path="/portal/playbook" element={<PlaybookPage />} />
                  <Route path="/portal/employee/settings" element={<EmployeeSettingsPage />} />
                  <Route path="/portal/login" element={<PortalLoginPage />} />
                  <Route path="/portal/magic" element={<PortalMagicPage />} />
                  <Route path="/portal/offer/:appId" element={<PortalOfferPage />} />
                  <Route path="/portal/docs/:appId" element={<PortalOfferPage />} />
                  <Route path="/portal/contract/:appId" element={<PortalOfferPage />} />
                  <Route path="/portal/g/:appId" element={<PortalGuestPage />} />
                  <Route path="/portal/signup" element={<PortalSignupPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/offers/accept/:token" element={<OfferAcceptPage />} />
                  {/* Iter 126 — becomelenders-style single-page deep-form
                      application. Auto-assigns to Mason. Route must be
                      registered BEFORE `/application/:slug` so `/application`
                      (no slug) matches this page rather than falling
                      through to the branded short-link resolver. */}
                  <Route path="/application" element={<LongApplicationPage />} />
                  {/* Iter 128 — post-submit landing page for the long-form
                      journey. Consumes the magic token minted by
                      `/applications/{id}/thank-you-login` and auto-logs
                      the customer into their portal. */}
                  <Route path="/thank-you" element={<ThankYouPage />} />
                  {/* Branded short link resolver. Maps to a friendly
                      powerforwardfunding.com/application/JOHNS-DELI URL
                      used in SMS + email reminders (Iteration 35). */}
                  <Route path="/application/:slug" element={<ApplicationSlugPage />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </AuthProvider>
        </ThemeProvider>
      </AppErrorBoundary>
      <Toaster theme="dark" position="top-right" />
    </div>
  );
}

export default App;
